import { SCREENS, SCREENS_MAX } from '../components/responsive';

export const Colors = {
  // ========================================
  // NEW DESIGN SYSTEM - Minimalist Black & White
  // Clean, Modern, Timeless
  // ========================================

  // Primary brand color. CSS variables, so one build serves every brand site:
  // Autopilotee black by default, Lenvoy navy when `applyBrandTheme()` runs
  // (src/app/utils/brandTheme.ts). Do not use these with a hex alpha suffix.
  Black: 'var(--color-black)',
  BlackHover: 'var(--color-black-hover)',
  BlackLight: 'var(--color-black-light)',
  OnPrimary: 'var(--color-on-primary)',
  Accent: 'var(--color-accent)',

  // Text Colors (Light Theme)
  TextPrimary: 'var(--color-text-primary)',
  TextSecondary: 'var(--color-text-secondary)',

  // Backgrounds (Light Theme)
  BgPrimary: 'var(--color-bg)',
  BgSecondary: 'var(--color-bg-secondary)',
  BgElevated: 'var(--color-bg-elevated)',
  BgWarm: 'var(--color-bg-secondary)',

  // Borders
  BorderLight: 'var(--color-border)',

  // ========================================
  // LEGACY ALIASES - Map old names to the brand primary
  // ========================================
  Void: 'var(--color-black)',
  VoidHover: 'var(--color-black-hover)',
  VoidLight: 'var(--color-black-light)',
  Charcoal: 'var(--color-black)',
  CharcoalHover: 'var(--color-black-hover)',
  CharcoalLight: 'var(--color-black-light)',
  Obsidian: 'var(--color-black)',
  ObsidianHover: 'var(--color-black-hover)',
  ObsidianLight: 'var(--color-black-light)',
  Burgundy: 'var(--color-black)',
  BurgundyHover: 'var(--color-black-hover)',
  BurgundyLight: 'var(--color-bg-secondary)',
  Champagne: 'var(--color-black)',
  ChampagneHover: 'var(--color-black-hover)',
  ChampagneLight: 'var(--color-bg-secondary)',
  Gold: 'var(--color-black)',
  GoldHover: 'var(--color-black-hover)',
  GoldLight: 'var(--color-bg-secondary)',
  GoldGrey: '#e0e0e0',
  BgIvory: 'var(--color-bg-secondary)',

  // ========================================
  // LEGACY COLORS - Keep for backward compatibility
  // ========================================

  // Brand Colors (Legacy)
  PrimaryRed: '#eb5a1c',
  PrimaryRedDark: '#C71585',
  PrimaryRedLight: '#f78f63',
  Red50: '#fef2f2',
  Red700: '#b91c1c',

  PrimaryIndigo: '#5a4af4',
  PrimaryIndigoLight: '#f1f0ff',
  PrimaryIndigoDark: '#1e1b4b',

  PrimaryBlueDark: '#1e3a8a',
  SecondaryBlue: '#1e3a8a',
  Blue50: '#eff6ff',
  Blue100: '#dbeafe',
  Blue500: '#3B82F6',
  Blue600: '#2563eb',
  Blue700: '#1d4ed8',
  Blue800: '#1e40af',

  Purple600: '#9333ea',
  Purple700: '#7e22ce',

  Yellow400: '#fbbf24',

  // Grays / Neutrals
  White: '#ffffff',
  Gray50: '#f9fafb',
  Gray100: '#f3f4f6',
  Gray200: '#e5e7eb',
  Gray300: '#d1d5db',
  Gray400: '#9ca3af',
  Gray500: '#6b7280',
  Gray600: '#4b5563',
  Gray700: '#374151',
  Gray800: '#1f2937',
  Gray900: '#111827',

  // Greens (Light to Dark)
  Green50: '#f0fdf4',
  Green100: '#dcfce7',
  Green200: '#bbf7d0',
  Green300: '#86efac',
  Green400: '#4ade80',
  Green500: '#22c55e',
  Green600: '#16a34a',
  Green700: '#15803d',
  Green800: '#166534',
  Green900: '#14532d',

  // Specific UI Elements
  TabActiveBg: '#3b3b3b',
  DateLabelInactive: '#9ca3af',
  BorderDefault: '#d1d5db',

  // States / Semantic
  Error: '#EF4444',
  LightRed: '#fee2e2',
  Success: '#10B981',
  Warning: '#F59E0B',
  Info: '#3B82F6',
  Pass: '#22C55E',
};

// ========================================
// Design Tokens - Minimalist Black & White
// ========================================

export const DesignTokens = {
  // Colors
  colors: {
    // Primary brand color (CSS variables; see src/app/utils/brandTheme.ts)
    primary: {
      default: 'var(--color-black)',
      hover: 'var(--color-black-hover)',
      light: 'var(--color-black-light)',
    },
    text: {
      primary: 'var(--color-text-primary)',
      secondary: 'var(--color-text-secondary)',
    },
    background: {
      primary: 'var(--color-bg)',
      secondary: 'var(--color-bg-secondary)',
      elevated: 'var(--color-bg-elevated)',
    },
    border: {
      default: 'var(--color-border)',
    },
    semantic: {
      success: '#10B981',
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#3B82F6',
    },
  },

  // Typography — per brand through CSS variables (Inter + Outfit by default,
  // Manrope on Lenvoy); Tailwind: font-sans / font-display
  typography: {
    fontFamily: 'var(--font-sans)',
    fontFamilyDisplay: 'var(--font-display)',
    fontSize: {
      display1: '36px',
      display1Md: '48px',
      display2: '28px',
      display2Md: '36px',
      title1: '24px',
      title1Md: '32px',
      title2: '20px',
      title2Md: '24px',
      title3: '18px',
      title3Md: '20px',
      body: '16px',
      bodySm: '14px',
      caption: '13px',
      label: '12px',

      // Backward compatibility aliases
      h1: '48px',
      h2: '32px',
      h3: '24px',
      cardTitle: '18px',
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      display1: 1.15,
      display2: 1.2,
      title1: 1.2,
      title2: 1.25,
      title3: 1.35,
      body: 1.5,
      caption: 1.45,
      label: 1.4,

      // Legacy compatibility
      tight: 1.2,
      normal: 1.4,
      relaxed: 1.6,
    },
  },

  // Spacing (4px base)
  spacing: {
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    6: '24px',
    8: '32px',
    12: '48px',
    16: '64px',
  },

  // Border Radius
  borderRadius: {
    tag: '4px',
    button: '8px',
    card: '12px',
    modal: '16px',
  },

  // Transitions
  transitions: {
    fast: '100ms ease-out',
    normal: '150ms ease-out',
    slow: '200ms ease-out',
    modal: '200ms ease-out',
  },

  // Z-Index
  zIndex: {
    base: 0,
    hoverCard: 10,
    dropdown: 50,
    sticky: 100,
    overlay: 200,
    modal: 300,
    toast: 400,
    max: 9999,
  },

  // Breakpoints — derived from canonical SCREENS (see responsive/index.ts)
  breakpoints: { ...SCREENS },
  breakpointsMax: { ...SCREENS_MAX },

  // Container
  container: {
    maxWidth: '1200px',
  },
};
